/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';

import SignupAuthenticationHelper from 'src/shared/app/signup/component/authentication/base/SignupAuthenticationHelper';
import messages from 'src/shared/app/signup/l10n/activationSignupL10n';
import { signupCodeFieldMaxLength } from 'src/shared/app/signup/settings/signupSettings';
import styles from 'src/shared/app/authentication/component/base/AuthenticationBase.style';
import AuthenticationFormHeader from 'src/shared/app/authentication/component/base/AuthenticationFormHeader';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import Markdown from 'src/shared/app/base/component/text/Markdown';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import NoteText from 'src/shared/ui/component/text/NoteText';

type Props = {
  eventName: string,
  legend: ?string,
  label: ?string,
  placeholder: ?string,
  note: ?string,
  handleSubmit: (Object) => Promise<any>,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const SignupActivationForm = ({
  eventName,
  legend,
  label,
  placeholder,
  note,
  handleSubmit,
  intl,
}: Props): React.Node => {
  const schema = Yup.object().shape({
    code: Yup.string().required(),
  });

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const noteElt = note && (
    <NoteText layoutClass={classes.footer}>
      <Markdown>{note}</Markdown>
    </NoteText>
  );

  return (
    <Formik
      initialValues={{ code: '' }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={(values, { setSubmitting, setStatus, setErrors }) =>
        handleSubmit(values).catch(({ errors }) => {
          setSubmitting(false);

          if (errors) {
            const { root } = errors;

            if (root) {
              setStatus(root);
            }

            setErrors(errors);
          }
        })
      }
    >
      {({ dirty, isSubmitting }) => (
        <Form className={classes.form} noValidate>
          <AuthenticationFormHeader>
            {legend || intl.formatMessage(messages.legend, { eventName })}
          </AuthenticationFormHeader>
          <Field
            id="code"
            name="code"
            type="text"
            component={FormikFormGroup}
            componentOpts={{
              label: label || intl.formatMessage(messages.label),
            }}
            maxLength={signupCodeFieldMaxLength}
            placeholder={placeholder}
            required
            autoComplete="off"
            autoFocus
          />
          <div className={classes.submit}>
            <SecondaryButton isDisabled={!dirty} isSubmitting={isSubmitting}>
              {intl.formatMessage(messages.submit)}
            </SecondaryButton>
          </div>
          <SignupAuthenticationHelper />
          {noteElt}
        </Form>
      )}
    </Formik>
  );
};

export default compose(React.memo, injectIntl)(SignupActivationForm);
